import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../App';

const SEAT_LIMITS = { starter: 1, growth: 2, pro: 5 };

// Hoisted to module scope (not defined inside Settings()) so React sees a
// stable component identity across renders — defining these inside the
// component gave them a new function reference on every keystroke anywhere
// on the page, which made React remount their underlying <input> elements
// and knocked focus out from under whatever the user was typing.
const Section = ({ title, children }) => (
  <div style={{ background: 'var(--card-bg)', borderRadius: 10, padding: 24, border: '1px solid var(--border)', marginBottom: 20 }}>
    <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 20 }}>{title}</h3>
    {children}
  </div>
);

const Field = ({ label, name, type = 'text', placeholder, hint, secret, value, onChange }) => (
  <div style={{ marginBottom: 16 }}>
    <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 5 }}>{label}</label>
    <input
      type={type} placeholder={secret ? '••••••• (leave blank to keep current)' : placeholder}
      value={value || ''}
      onChange={onChange}
      style={{
        width: '100%', maxWidth: 420, padding: '9px 12px',
        border: '1px solid var(--border)', borderRadius: 7, fontSize: 14, fontFamily: 'inherit',
        background: 'var(--card-bg)', color: 'var(--vela-text)',
      }}
    />
    {hint && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>{hint}</div>}
  </div>
);

const STATUS_LABELS = {
  trialing:  { text: 'Free trial', color: '#93c5fd', bg: 'rgba(59,130,246,0.15)' },
  active:    { text: 'Active',     color: '#4ade80', bg: 'rgba(34,197,94,0.15)' },
  past_due:  { text: 'Payment failed', color: '#f87171', bg: 'rgba(220,38,38,0.15)' },
  canceled:  { text: 'Canceled',   color: '#fbbf24', bg: 'rgba(217,119,6,0.15)' },
  none:      { text: 'No subscription', color: '#fbbf24', bg: 'rgba(217,119,6,0.15)' },
};
const fmtDate = d => d ? new Date(d).toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' }) : '—';

const BillingPanel = ({ billing, billingFlash, billingBusy, plan, openPortal, startCheckout }) => {
  const b = billing || {};
  const status = b.subscription_status || 'none';
  const badge = STATUS_LABELS[status] || STATUS_LABELS.none;
  const planName = (b.plan || plan || 'starter');
  const planTitle = planName.charAt(0).toUpperCase() + planName.slice(1);
  // A Stripe customer can exist (has_billing) from an abandoned checkout attempt
  // with no real subscription — gate on subscription_status, not customer existence.
  const hasSubscription = ['trialing', 'active', 'past_due'].includes(status);

  return (
    <div style={{ background: 'var(--card-bg)', borderRadius: 10, padding: 24, border: '1px solid var(--border)', marginBottom: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ fontSize: 15, fontWeight: 600 }}>Billing &amp; Subscription</h3>
        <span style={{ fontSize: 12, fontWeight: 600, padding: '4px 10px', borderRadius: 20, color: badge.color, background: badge.bg }}>
          {badge.text}
        </span>
      </div>

      {billingFlash === 'success' && (
        <div style={{ background: 'rgba(34,197,94,0.15)', color: '#4ade80', borderRadius: 7, padding: '10px 14px', marginBottom: 16, fontSize: 13 }}>
          ✓ Your subscription is set up. Welcome aboard!
        </div>
      )}
      {billingFlash === 'cancelled' && (
        <div style={{ background: 'rgba(217,119,6,0.15)', color: '#fbbf24', borderRadius: 7, padding: '10px 14px', marginBottom: 16, fontSize: 13 }}>
          Checkout was cancelled — you have not been charged.
        </div>
      )}

      {b.billing_configured === false && (
        <div style={{ background: 'rgba(217,119,6,0.15)', color: '#fbbf24', borderRadius: 7, padding: '10px 14px', marginBottom: 16, fontSize: 13 }}>
          ⚠ Billing is not connected yet. Once Stripe is configured, plans and payments activate here automatically.
        </div>
      )}

      <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap', marginBottom: 18 }}>
        <div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Current plan</div>
          <div style={{ fontSize: 18, fontWeight: 700 }}>{planTitle}</div>
        </div>
        <div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Contacts used</div>
          <div style={{ fontSize: 18, fontWeight: 700 }}>
            {b.contact_count != null ? b.contact_count : '—'}
            <span style={{ fontSize: 13, fontWeight: 400, color: 'var(--text-muted)' }}>
              {' '}/ {b.contact_limit != null ? b.contact_limit : 'unlimited'}
            </span>
          </div>
        </div>
        <div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            {status === 'trialing' ? 'Trial ends' : 'Renews'}
          </div>
          <div style={{ fontSize: 18, fontWeight: 700 }}>
            {status === 'trialing' ? fmtDate(b.trial_ends_at) : fmtDate(b.current_period_end)}
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        {hasSubscription ? (
          <button onClick={openPortal} disabled={billingBusy} style={{
            background: 'var(--primary)', color: 'var(--primary-contrast)', border: 'none', borderRadius: 7,
            padding: '9px 18px', cursor: 'pointer', fontSize: 14, fontWeight: 600, opacity: billingBusy ? 0.7 : 1,
          }}>
            {billingBusy ? 'Opening…' : 'Manage billing'}
          </button>
        ) : (
          ['starter', 'growth', 'pro'].map(p => (
            <button key={p} onClick={() => startCheckout(p)} disabled={billingBusy} style={{
              background: p === planName ? 'var(--primary)' : 'transparent',
              color: p === planName ? 'var(--primary-contrast)' : 'var(--primary)',
              border: '1px solid var(--primary)', borderRadius: 7,
              padding: '9px 18px', cursor: 'pointer', fontSize: 14, fontWeight: 600, opacity: billingBusy ? 0.7 : 1,
            }}>
              {p === planName ? `Subscribe — ${planTitle}` : `Switch to ${p.charAt(0).toUpperCase() + p.slice(1)}`}
            </button>
          ))
        )}
      </div>
      <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 12 }}>
        Update your card, change plan, or cancel anytime from the billing portal. Prices in CAD; GST/HST added where applicable.
      </p>
    </div>
  );
};

export default function Settings() {
  const { switchAccount } = useAuth();
  const [form, setForm]       = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [saved, setSaved]     = useState(false);
  const [billing, setBilling] = useState(null);
  const [billingBusy, setBillingBusy] = useState(false);
  const [team, setTeam]       = useState([]);
  const [teamError, setTeamError] = useState('');
  const [invite, setInvite]   = useState({ email: '' });
  const [inviteBusy, setInviteBusy] = useState(false);
  const [inviteNotice, setInviteNotice] = useState('');
  const [pending, setPending] = useState([]);
  const [newBiz, setNewBiz]   = useState({ name: '', owner_name: '' });
  const [newBizBusy, setNewBizBusy] = useState(false);
  const [newBizError, setNewBizError] = useState('');

  const loadTeam = () => {
    axios.get('/api/accounts/team').then(r => setTeam(r.data)).catch(() => {});
    axios.get('/api/accounts/team/invites').then(r => setPending(r.data)).catch(() => {});
  };

  useEffect(() => {
    axios.get('/api/accounts/me').then(r => {
      setForm(r.data);
    }).catch(() => setLoadError(true))
      .finally(() => setLoading(false));
    axios.get('/api/billing/status')
      .then(r => setBilling(r.data))
      .catch(() => setBilling({ billing_configured: false }));
    loadTeam();
  }, []);

  const inviteMember = async e => {
    e.preventDefault();
    setInviteBusy(true); setTeamError(''); setInviteNotice('');
    try {
      const { data } = await axios.post('/api/accounts/team/invite', { email: invite.email });
      setInvite({ email: '' });
      setInviteNotice(data.email_sent === false
        ? (data.warning || 'Invitation created, but the email could not be sent.')
        : `Invitation sent to ${invite.email}. It expires in 7 days.`);
      axios.get('/api/accounts/team/invites').then(r => setPending(r.data)).catch(() => {});
    } catch (err) {
      setTeamError(err.response?.data?.error || 'Could not send the invitation');
    } finally {
      setInviteBusy(false);
    }
  };

  const withdrawInvite = async id => {
    if (!window.confirm('Withdraw this invitation? The link in that email will stop working.')) return;
    try {
      await axios.delete(`/api/accounts/team/invites/${id}`);
      setPending(p => p.filter(i => i.id !== id));
    } catch (err) {
      setTeamError(err.response?.data?.error || 'Could not withdraw the invitation');
    }
  };

  const removeMember = async userId => {
    if (!window.confirm('Remove this team member from the account?')) return;
    try {
      await axios.delete(`/api/accounts/team/${userId}`);
      loadTeam();
    } catch (err) {
      setTeamError(err.response?.data?.error || 'Could not remove team member');
    }
  };

  const createBusiness = async e => {
    e.preventDefault();
    setNewBizBusy(true); setNewBizError('');
    try {
      const { data } = await axios.post('/api/accounts', newBiz);
      await switchAccount(data.id);
    } catch (err) {
      setNewBizError(err.response?.data?.error || 'Could not create account');
    } finally {
      setNewBizBusy(false);
    }
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Banner from Stripe Checkout redirect (?billing=success|cancelled)
  const billingFlash = new URLSearchParams(window.location.search).get('billing');

  const openPortal = async () => {
    setBillingBusy(true);
    try {
      const { data } = await axios.post('/api/billing/portal');
      if (data.url) window.location.href = data.url;
    } catch (err) {
      alert(err.response?.data?.error || 'Could not open billing portal');
    } finally { setBillingBusy(false); }
  };

  const startCheckout = async (plan) => {
    setBillingBusy(true);
    try {
      const { data } = await axios.post('/api/billing/checkout', { plan });
      if (data.url) window.location.href = data.url;
    } catch (err) {
      alert(err.response?.data?.error || 'Could not start checkout');
    } finally { setBillingBusy(false); }
  };

  const handleSave = async e => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.patch('/api/accounts/me', {
        name: form.name, owner_name: form.owner_name,
        reply_to_email: form.reply_to_email, booking_link: form.booking_link,
        lead_magnet_link: form.lead_magnet_link, business_address: form.business_address,
        sendgrid_key: form.sendgrid_key_input,
        twilio_sid: form.twilio_sid_input, twilio_token: form.twilio_token_input,
        twilio_from: form.twilio_from,
        hubspot_token: form.hubspot_token_input,
        zapier_webhook: form.zapier_webhook,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={{ color: 'var(--text-muted)' }}>Loading settings...</div>;
  if (loadError) return <div style={{ color: 'var(--text-muted)' }}>Couldn't load your settings. Please refresh the page.</div>;

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700 }}>Settings</h1>
        <p style={{ color: 'var(--text-muted)', marginTop: 2 }}>Configure your account and integrations</p>
      </div>

      <BillingPanel
        billing={billing} billingFlash={billingFlash} billingBusy={billingBusy}
        plan={form.plan} openPortal={openPortal} startCheckout={startCheckout}
      />

      <div style={{ background: 'var(--card-bg)', borderRadius: 10, padding: 24, border: '1px solid var(--border)', marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ fontSize: 15, fontWeight: 600 }}>Team</h3>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            {team.length} / {SEAT_LIMITS[form.plan] || 1} seats
          </span>
        </div>

        {teamError && (
          <div style={{ background: 'rgba(220,38,38,0.15)', color: 'var(--danger)', borderRadius: 7, padding: '10px 14px', marginBottom: 16, fontSize: 13 }}>
            {teamError}
          </div>
        )}

        {team.map(m => (
          <div key={m.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
            <div>
              <span style={{ fontWeight: 500 }}>{m.name || m.email}</span>
              <span style={{ color: 'var(--text-muted)', marginLeft: 8 }}>{m.email}</span>
              <span style={{
                marginLeft: 10, fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
                color: m.role === 'owner' ? 'var(--primary)' : 'var(--text-muted)',
              }}>{m.role}</span>
            </div>
            {form.role === 'owner' && m.role !== 'owner' && (
              <button onClick={() => removeMember(m.id)} style={{
                background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer', fontSize: 12,
              }}>Remove</button>
            )}
          </div>
        ))}

        {form.role === 'owner' && (
          <form onSubmit={inviteMember} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 16 }}>
            <input required type="email" placeholder="teammate@business.ca" value={invite.email}
              onChange={e => setInvite({ email: e.target.value })}
              style={{ padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 13, background: 'var(--card-bg)', color: 'var(--vela-text)', flex: '1 1 220px' }} />
            <button type="submit" disabled={inviteBusy} style={{
              background: 'var(--primary)', color: 'var(--primary-contrast)', border: 'none', borderRadius: 6,
              padding: '8px 16px', cursor: 'pointer', fontSize: 13, fontWeight: 600,
            }}>{inviteBusy ? 'Sending…' : 'Send invitation'}</button>
          </form>
        )}

        {inviteNotice && (
          <p style={{ fontSize: 12, color: 'var(--vela-text)', marginTop: 10 }}>{inviteNotice}</p>
        )}

        {pending.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>Awaiting acceptance</p>
            {pending.map(i => (
              <div key={i.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderTop: '1px solid var(--border)' }}>
                <span style={{ fontSize: 13 }}>{i.email}</span>
                <button onClick={() => withdrawInvite(i.id)} style={{
                  background: 'none', border: '1px solid var(--border)', borderRadius: 6,
                  padding: '4px 10px', cursor: 'pointer', fontSize: 12, color: 'var(--text-muted)',
                }}>Withdraw</button>
              </div>
            ))}
          </div>
        )}
        <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 10 }}>
          They'll get an email with a link to join. They choose their own password — you never handle it.
        </p>
      </div>

      {form.role === 'owner' && form.plan === 'pro' && (
        <div style={{ background: 'var(--card-bg)', borderRadius: 10, padding: 24, border: '1px solid var(--border)', marginBottom: 20 }}>
          <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>Manage Multiple Businesses</h3>
          <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 16 }}>
            Your Pro plan covers unlimited additional business accounts — no separate billing per business.
          </p>
          {newBizError && (
            <div style={{ background: 'rgba(220,38,38,0.15)', color: 'var(--danger)', borderRadius: 7, padding: '10px 14px', marginBottom: 16, fontSize: 13 }}>
              {newBizError}
            </div>
          )}
          <form onSubmit={createBusiness} style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <input required placeholder="Business name" value={newBiz.name}
              onChange={e => setNewBiz(b => ({ ...b, name: e.target.value }))}
              style={{ padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 13, background: 'var(--card-bg)', color: 'var(--vela-text)', flex: '1 1 160px' }} />
            <input placeholder="Owner name (optional)" value={newBiz.owner_name}
              onChange={e => setNewBiz(b => ({ ...b, owner_name: e.target.value }))}
              style={{ padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 13, background: 'var(--card-bg)', color: 'var(--vela-text)', flex: '1 1 140px' }} />
            <button type="submit" disabled={newBizBusy} style={{
              background: 'var(--primary)', color: 'var(--primary-contrast)', border: 'none', borderRadius: 6,
              padding: '8px 16px', cursor: 'pointer', fontSize: 13, fontWeight: 600,
            }}>{newBizBusy ? 'Creating…' : 'Add business'}</button>
          </form>
        </div>
      )}

      <form onSubmit={handleSave}>
        <Section title="Business Details">
          <Field label="Business Name"    value={form.name} onChange={e => set('name', e.target.value)} placeholder="Halifax Mortgage Co." />
          <Field label="Your Name"        value={form.owner_name} onChange={e => set('owner_name', e.target.value)} placeholder="Sarah MacDonald" />
          <Field label="Reply-To Email"   value={form.reply_to_email} onChange={e => set('reply_to_email', e.target.value)} placeholder="sarah@halifaxmortgage.ca"
            hint="All emails will appear to come from this address" />
          <Field label="Business Address" value={form.business_address} onChange={e => set('business_address', e.target.value)} placeholder="Halifax, Nova Scotia, Canada"
            hint="Used in email footers for CASL compliance" />
          <Field label="Booking Link"     value={form.booking_link} onChange={e => set('booking_link', e.target.value)} placeholder="https://calendly.com/yourlink"
            hint="This will be inserted into emails as {{booking_link}}" />
          <Field label="Lead Magnet Link" value={form.lead_magnet_link} onChange={e => set('lead_magnet_link', e.target.value)} placeholder="https://yoursite.ca/guide.pdf"
            hint="Delivered in the Day 1 welcome email" />
        </Section>

        <Section title="Email — SendGrid">
          <div style={{ background: 'var(--primary-light)', borderRadius: 7, padding: 12, marginBottom: 16, fontSize: 13, color: '#E6E8EB' }}>
            {form.has_sendgrid ? '✓ SendGrid API key configured' : '⚠ No SendGrid key — emails will not send'}
          </div>
          <Field label="SendGrid API Key" value={form.sendgrid_key_input} onChange={e => set('sendgrid_key_input', e.target.value)} secret
            hint="Get your API key from sendgrid.com → Settings → API Keys" />
        </Section>

        <Section title="SMS — Twilio">
          <div style={{ background: form.has_twilio ? 'var(--primary-light)' : 'rgba(217,119,6,0.15)', borderRadius: 7, padding: 12, marginBottom: 16, fontSize: 13, color: form.has_twilio ? '#E6E8EB' : '#fbbf24' }}>
            {form.has_twilio ? '✓ Twilio configured' : '⚠ Twilio not set up — SMS will be skipped'}
          </div>
          <Field label="Twilio Account SID" value={form.twilio_sid_input} onChange={e => set('twilio_sid_input', e.target.value)} secret />
          <Field label="Twilio Auth Token"  value={form.twilio_token_input} onChange={e => set('twilio_token_input', e.target.value)} secret />
          <Field label="Twilio From Number" value={form.twilio_from} onChange={e => set('twilio_from', e.target.value)} placeholder="+19025551234"
            hint="The Twilio phone number to send SMS from" />
        </Section>

        <Section title="CRM Integration">
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 8 }}>HubSpot (Free CRM)</label>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>
              Get a Private App token from HubSpot → Settings → Integrations → Private Apps
            </div>
            <Field label="HubSpot Private App Token" value={form.hubspot_token_input} onChange={e => set('hubspot_token_input', e.target.value)} secret />
          </div>
          <div>
            <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 8 }}>Zapier Webhook (for all other CRMs)</label>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>
              Create a "Catch Hook" trigger in Zapier and paste the URL here.
              All sales-ready lead data will be sent as JSON.
            </div>
            <Field label="Zapier Webhook URL" value={form.zapier_webhook} onChange={e => set('zapier_webhook', e.target.value)} placeholder="https://hooks.zapier.com/hooks/catch/..." />
          </div>
        </Section>

        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <button type="submit" disabled={saving} style={{
            background: 'var(--primary)', color: 'var(--primary-contrast)', border: 'none',
            borderRadius: 7, padding: '10px 24px', cursor: 'pointer',
            fontSize: 14, fontWeight: 600, opacity: saving ? 0.7 : 1,
          }}>
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
          {saved && <span style={{ color: 'var(--success)', fontSize: 14 }}>✓ Saved</span>}
        </div>
      </form>

      {/* Embed code */}
      <div style={{ background: 'var(--card-bg)', borderRadius: 10, padding: 24, border: '1px solid var(--border)', marginTop: 20 }}>
        <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 12 }}>Lead Capture Form — Embed Code</h3>
        <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 12 }}>
          Paste this script on your website to embed the capture form. Replace YOUR_API_KEY with your key below.
        </p>
        <code style={{
          display: 'block', background: 'rgba(0,0,0,0.3)', color: '#A6E3D0',
          padding: 16, borderRadius: 8, fontSize: 12, lineHeight: 1.6,
          overflowX: 'auto', whiteSpace: 'pre-wrap',
        }}>{`<script src="${process.env.REACT_APP_API_URL || 'https://yourapi.com'}/embed.js" data-api-key="${form.api_key || 'YOUR_API_KEY'}" async></script>`}</code>
      </div>
    </div>
  );
}
